import { useCallback, useEffect, useRef } from 'react';

const MESSAGE_TYPE = 'project:update';

function channelName(projectId) {
  return `communityshare:project:${projectId}`;
}

/**
 * Cross-tab updates for a project over the BroadcastChannel API. The share
 * dialog broadcasts field changes; the public share tab listens and reacts.
 *
 * Message shape: { type: 'project:update', projectId, fields?: { title, imageUrl } }
 *
 * `fields` is optional: a message with specific fields lets a listener apply
 * them optimistically, while a fieldless message is a generic "this project
 * changed, re-pull it" signal (e.g. broadcast by the manage dialog, which knows
 * the record changed but not which public fields it maps to).
 *
 * @param {number|string} projectId
 * @param {(message: { type: string, projectId: number|string, fields?: object }) => void} [onMessage]
 *   Called for each incoming message. Omit on send-only senders.
 * @returns {(fields?: { title?: string, imageUrl?: string }) => void} broadcast
 */
export default function useProjectChannel(projectId, onMessage) {
  const channelRef = useRef(null);

  // Keep the latest callback without re-subscribing the channel each render.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!projectId || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    const channel = new BroadcastChannel(channelName(projectId));
    channelRef.current = channel;
    channel.onmessage = (event) => {
      if (onMessageRef.current) {
        onMessageRef.current(event.data);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [projectId]);

  return useCallback((fields) => {
    const channel = channelRef.current;
    if (channel) {
      channel.postMessage({ type: MESSAGE_TYPE, projectId, fields });
    }
  }, [projectId]);
}
